// Arena FRAME sections 0x10–0x16 (protocol §5.4, docs/territory-arena.md §11). Arena rooms only;
// their layouts are in the doc comments of the record types in types.ts and in docs/protocol.md.
//
// - Every section writer emits exactly one section (`beginSection` / `endSection` from frame.ts;
//   the frame.ts ↔ this file import cycle only involves hoisted functions, so it is safe) and
//   throws RangeError past the MAX_ARENA_* limits or on malformed input (chunk runs that do not
//   cover 256 cells, a progress outside 0–1 is clamped instead).
// - Every reader gets a Reader bounded to the section payload, rejects counts that cannot fit
//   the remaining bytes and the MAX_ARENA_* limits with ProtocolError, and ignores unread
//   trailing bytes (so later fields can be appended without a new section).
// - `writeFrame` / `readFrameBody` call these in ascending id order.
// - Low-level record writers for the server's per-tick shared cache (protocol §6.4) are here
//   too: `writeArenaChunkRecord`, `writeArenaHeadRecordQ`, `writeArenaTrailRecordFrom`,
//   `writeArenaEventRecord`, `writeArenaMinimapHeadRecord`. The section framing (flags, counts)
//   is then the builder's.
// - Budget: protocol §6.6, measured on a 30-player room by `bench:arena:net` (backlog P-6).
//
// Owner: lane P.

import { ARENA_DEATH_CAUSES, type ArenaDeathCause } from '../arena/constants.js';
import {
  MAX_ARENA_CHUNKS,
  MAX_ARENA_DROPS,
  MAX_ARENA_EVENTS,
  MAX_ARENA_HEADS,
  MAX_ARENA_TRAIL_RECORDS,
  MAX_ARENA_TRAIL_STEPS,
} from '../arena/constants.js';
import { beginSection, endSection } from './frame.js';
import {
  ARENA_CAUSE_CODE,
  ARENA_EVENT,
  ARENA_EXIT_CODE,
  ARENA_HEAD_FLAG,
  ARENA_LAND_FLAG,
  ARENA_TRAIL_OP,
  type ArenaCauseCode,
  type ArenaExitCode,
  type ArenaTrailOp,
  SECTION,
} from './opcodes.js';
import { ProtocolError, type Reader } from './reader.js';
import type {
  ArenaDropAdd,
  ArenaDropRemove,
  ArenaDropsSection,
  ArenaEventRecord,
  ArenaHeadRecord,
  ArenaLandChunk,
  ArenaLandSection,
  ArenaMinimapHead,
  ArenaMinimapSection,
  ArenaOwnSection,
  ArenaTrailRecord,
} from './types.js';
import type { Writer } from './writer.js';

/** Cells per land chunk (ARENA_NET.chunkSize²). */
const CHUNK_CELLS = 256;
/** Head positions: 1/256 cell (ARENA_NET.posSteps). */
const POS_STEPS = 256;
/** Cash-out progress on the wire: × 10000. */
const PROGRESS_STEPS = 10_000;
/** ARENA_MINIMAP size limit (u8). */
const MAX_MINIMAP_SIZE = 255;

const HEAD_KNOWN_FLAGS =
  ARENA_HEAD_FLAG.SHIELDED |
  ARENA_HEAD_FLAG.CASHING_OUT |
  ARENA_HEAD_FLAG.FROZEN |
  ARENA_HEAD_FLAG.AUTOPILOT |
  ARENA_HEAD_FLAG.ALARM;

// ---------------------------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------------------------

/** ArenaDeathCause → its wire code (ARENA_CAUSE_CODE). */
export function arenaCauseCode(cause: ArenaDeathCause): ArenaCauseCode {
  switch (cause) {
    case 'cut':
      return ARENA_CAUSE_CODE.CUT;
    case 'enclosed':
      return ARENA_CAUSE_CODE.ENCLOSED;
    case 'starved':
      return ARENA_CAUSE_CODE.STARVED;
    case 'self':
      return ARENA_CAUSE_CODE.SELF;
    case 'exitCaptured':
      return ARENA_CAUSE_CODE.EXIT_CAPTURED;
  }
}

/** A wire cause code → ArenaDeathCause (null for NONE or an unknown code). */
export function arenaCauseFromCode(code: number): ArenaDeathCause | null {
  return code >= 1 && code <= ARENA_DEATH_CAUSES.length
    ? (ARENA_DEATH_CAUSES[code - 1] as ArenaDeathCause)
    : null;
}

const MAX_CAUSE_CODE: number = ARENA_CAUSE_CODE.EXIT_CAPTURED;
const MAX_EXIT_CODE: number = ARENA_EXIT_CODE.SHUTDOWN;
const MAX_TRAIL_OP: number = ARENA_TRAIL_OP.CLEAR;

// ---------------------------------------------------------------------------------------------
// RLE
// ---------------------------------------------------------------------------------------------

/** RLE of a cell sequence (ARENA_LAND chunks, ARENA_MINIMAP): flat `[length, value, …]`. */
export function rleRuns(values: ArrayLike<number>): number[] {
  const out: number[] = [];
  if (values.length === 0) return out;
  let cur = values[0] as number;
  let len = 1;
  for (let i = 1; i < values.length; i++) {
    const v = values[i] as number;
    if (v === cur) {
      len++;
    } else {
      out.push(len, cur);
      cur = v;
      len = 1;
    }
  }
  out.push(len, cur);
  return out;
}

/**
 * Expands flat RLE runs into `out` from index 0 (length = Σ lengths). Throws RangeError if the
 * runs are longer than `out`.
 */
export function expandRuns(runs: readonly number[], out: Uint16Array | number[]): void {
  let k = 0;
  const cap = out.length;
  for (let i = 0; i + 1 < runs.length; i += 2) {
    const len = runs[i] as number;
    const v = runs[i + 1] as number;
    if (k + len > cap) throw new RangeError(`runs cover more than ${cap} cells`);
    for (let j = 0; j < len; j++) out[k++] = v;
  }
}

function runsTotal(runs: readonly number[]): number {
  let n = 0;
  for (let i = 0; i < runs.length; i += 2) n += runs[i] as number;
  return n;
}

function writeRuns(w: Writer, runs: readonly number[], what: string): void {
  if (runs.length % 2 !== 0) throw new RangeError(`${what} runs must be [length, id] pairs`);
  w.vu(runs.length / 2);
  for (let i = 0; i < runs.length; i += 2) {
    const len = runs[i] as number;
    if (!(len > 0)) throw new RangeError(`${what} run length must be ≥ 1: ${len}`);
    w.vu(len).vu(runs[i + 1] as number);
  }
}

/** Reads `vu pairCount` and the pairs; each run ≥ 1 and the total must equal `total`. */
function readRuns(r: Reader, total: number, what: string): number[] {
  const at = r.offset;
  const pairs = r.vu();
  if (pairs > total) throw new ProtocolError(`${what} has ${pairs} runs for ${total} cells`, at);
  if (pairs * 2 > r.remaining) throw new ProtocolError(`${what} run count exceeds the data`, at);
  const runs: number[] = [];
  let sum = 0;
  for (let i = 0; i < pairs; i++) {
    const lenAt = r.offset;
    const len = r.vu();
    if (len === 0 || sum + len > total) {
      throw new ProtocolError(`${what} runs exceed ${total} cells`, lenAt);
    }
    sum += len;
    runs.push(len, r.vu());
  }
  if (sum !== total) throw new ProtocolError(`${what} runs cover ${sum} of ${total} cells`, at);
  return runs;
}

/** A `vu` count checked against `max` and the remaining bytes (`minRecordBytes` each). */
function readCount(r: Reader, max: number, minRecordBytes: number, what: string): number {
  const at = r.offset;
  const n = r.vu();
  if (n > max) throw new ProtocolError(`${what} count ${n} > ${max}`, at);
  if (n * minRecordBytes > r.remaining)
    throw new ProtocolError(`${what} count ${n} exceeds the data`, at);
  return n;
}

function checkCount(n: number, max: number, what: string): void {
  if (n > max) throw new RangeError(`${what} has ${n} records (max ${max})`);
}

// ---------------------------------------------------------------------------------------------
// ARENA_LAND (0x10)
// ---------------------------------------------------------------------------------------------

/** One chunk record: `vu chunk`, `vu pairCount`, pairs. Runs must sum to 256. */
export function writeArenaChunkRecord(w: Writer, chunk: number, runs: readonly number[]): void {
  if (runsTotal(runs) !== CHUNK_CELLS) {
    throw new RangeError(`chunk ${chunk} runs cover ${runsTotal(runs)} cells, not 256`);
  }
  w.vu(chunk);
  writeRuns(w, runs, 'ARENA_LAND chunk');
}

export function writeArenaLandSection(w: Writer, land: ArenaLandSection): void {
  checkCount(land.chunks.length, MAX_ARENA_CHUNKS, 'ARENA_LAND');
  const s = beginSection(w, SECTION.ARENA_LAND);
  w.u8(land.snapshot ? ARENA_LAND_FLAG.SNAPSHOT : 0).vu(land.chunks.length);
  for (const c of land.chunks) writeArenaChunkRecord(w, c.chunk, c.runs);
  endSection(w, s);
}

export function readArenaLandSection(r: Reader): ArenaLandSection {
  const flags = r.u8();
  const n = readCount(r, MAX_ARENA_CHUNKS, 4, 'ARENA_LAND');
  const chunks: ArenaLandChunk[] = [];
  for (let i = 0; i < n; i++) {
    const chunk = r.vu();
    chunks.push({ chunk, runs: readRuns(r, CHUNK_CELLS, 'ARENA_LAND chunk') });
  }
  return { snapshot: (flags & ARENA_LAND_FLAG.SNAPSHOT) !== 0, chunks };
}

// ---------------------------------------------------------------------------------------------
// ARENA_HEADS (0x11)
// ---------------------------------------------------------------------------------------------

/** A position (cells) in wire steps: round(v · 256), never negative. */
export function arenaPosToWire(v: number): number {
  const q = Number.isFinite(v) ? Math.round(v * POS_STEPS) : 0;
  return q < 0 ? 0 : q > 0xffffffff ? 0xffffffff : q;
}

/** Cash-out progress (0–1) as `u16` × 10000, clamped. */
export function arenaProgressToWire(p: number): number {
  const q = Number.isFinite(p) ? Math.round(p * PROGRESS_STEPS) : 0;
  return q < 0 ? 0 : q > PROGRESS_STEPS ? PROGRESS_STEPS : q;
}

/** The ARENA_HEAD_FLAG bits of a record. */
export function arenaHeadFlags(
  h: Omit<ArenaHeadRecord, 'playerId' | 'x' | 'y' | 'heading' | 'progress'>,
): number {
  return (
    (h.shielded ? ARENA_HEAD_FLAG.SHIELDED : 0) |
    (h.cashingOut ? ARENA_HEAD_FLAG.CASHING_OUT : 0) |
    (h.frozen ? ARENA_HEAD_FLAG.FROZEN : 0) |
    (h.autopilot ? ARENA_HEAD_FLAG.AUTOPILOT : 0) |
    (h.alarm ? ARENA_HEAD_FLAG.ALARM : 0)
  );
}

/**
 * One head record from wire values: `qx`, `qy` in 1/256 cell, `heading` in u16 steps, `flags`
 * (ARENA_HEAD_FLAG), `progressQ` (× 10000, written iff CASHING_OUT).
 */
export function writeArenaHeadRecordQ(
  w: Writer,
  playerId: number,
  qx: number,
  qy: number,
  heading: number,
  flags: number,
  progressQ: number,
): void {
  w.vu(playerId)
    .vu(qx)
    .vu(qy)
    .u16(heading & 0xffff)
    .u8(flags & HEAD_KNOWN_FLAGS);
  if ((flags & ARENA_HEAD_FLAG.CASHING_OUT) !== 0) w.u16(progressQ);
}

export function writeArenaHeadRecord(w: Writer, h: ArenaHeadRecord): void {
  writeArenaHeadRecordQ(
    w,
    h.playerId,
    arenaPosToWire(h.x),
    arenaPosToWire(h.y),
    h.heading,
    arenaHeadFlags(h),
    h.cashingOut ? arenaProgressToWire(h.progress) : 0,
  );
}

export function writeArenaHeadsSection(w: Writer, heads: readonly ArenaHeadRecord[]): void {
  checkCount(heads.length, MAX_ARENA_HEADS, 'ARENA_HEADS');
  const s = beginSection(w, SECTION.ARENA_HEADS);
  w.vu(heads.length);
  for (const h of heads) writeArenaHeadRecord(w, h);
  endSection(w, s);
}

export function readArenaHeadsSection(r: Reader): ArenaHeadRecord[] {
  const n = readCount(r, MAX_ARENA_HEADS, 6, 'ARENA_HEADS');
  const out: ArenaHeadRecord[] = [];
  for (let i = 0; i < n; i++) {
    const playerId = r.vu();
    const x = r.vu() / POS_STEPS;
    const y = r.vu() / POS_STEPS;
    const heading = r.u16();
    const flags = r.u8();
    const cashingOut = (flags & ARENA_HEAD_FLAG.CASHING_OUT) !== 0;
    const progress = cashingOut ? Math.min(1, r.u16() / PROGRESS_STEPS) : 0;
    out.push({
      playerId,
      x,
      y,
      heading,
      shielded: (flags & ARENA_HEAD_FLAG.SHIELDED) !== 0,
      cashingOut,
      frozen: (flags & ARENA_HEAD_FLAG.FROZEN) !== 0,
      autopilot: (flags & ARENA_HEAD_FLAG.AUTOPILOT) !== 0,
      alarm: (flags & ARENA_HEAD_FLAG.ALARM) !== 0,
      progress,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// ARENA_TRAILS (0x12)
// ---------------------------------------------------------------------------------------------

/** `vu n` then ceil(n / 4) bytes of 2-bit steps (first step in the low bits), `steps[from … to)`. */
function writeSteps(w: Writer, steps: ArrayLike<number>, from: number, to: number): void {
  const n = to - from;
  if (n > MAX_ARENA_TRAIL_STEPS) {
    throw new RangeError(`ARENA_TRAILS record has ${n} steps (max ${MAX_ARENA_TRAIL_STEPS})`);
  }
  w.vu(n);
  for (let i = from; i < to; i += 4) {
    let b = 0;
    for (let j = 0; j < 4 && i + j < to; j++) b |= ((steps[i + j] as number) & 3) << (2 * j);
    w.u8(b);
  }
}

/**
 * One trail record: `vu playerId`, `u8 op`; RESET adds `vu startCell`; APPEND and RESET add the
 * steps `steps[from … to)`.
 */
export function writeArenaTrailRecordFrom(
  w: Writer,
  playerId: number,
  op: ArenaTrailOp,
  startCell: number,
  steps: ArrayLike<number>,
  from = 0,
  to = steps.length,
): void {
  w.vu(playerId).u8(op);
  if (op === ARENA_TRAIL_OP.RESET) w.vu(startCell);
  if (op !== ARENA_TRAIL_OP.CLEAR) writeSteps(w, steps, from, to);
}

export function writeArenaTrailsSection(w: Writer, trails: readonly ArenaTrailRecord[]): void {
  checkCount(trails.length, MAX_ARENA_TRAIL_RECORDS, 'ARENA_TRAILS');
  const s = beginSection(w, SECTION.ARENA_TRAILS);
  w.vu(trails.length);
  for (const t of trails) writeArenaTrailRecordFrom(w, t.playerId, t.op, t.startCell, t.steps);
  endSection(w, s);
}

export function readArenaTrailsSection(r: Reader): ArenaTrailRecord[] {
  const n = readCount(r, MAX_ARENA_TRAIL_RECORDS, 2, 'ARENA_TRAILS');
  const out: ArenaTrailRecord[] = [];
  for (let i = 0; i < n; i++) {
    const playerId = r.vu();
    const opAt = r.offset;
    const op = r.u8();
    if (op > MAX_TRAIL_OP) throw new ProtocolError(`unknown ARENA_TRAILS op ${op}`, opAt);
    const startCell = op === ARENA_TRAIL_OP.RESET ? r.vu() : 0;
    const steps: number[] = [];
    if (op !== ARENA_TRAIL_OP.CLEAR) {
      const nAt = r.offset;
      const k = r.vu();
      if (k > MAX_ARENA_TRAIL_STEPS) {
        throw new ProtocolError(`ARENA_TRAILS steps ${k} > ${MAX_ARENA_TRAIL_STEPS}`, nAt);
      }
      const bytes = Math.ceil(k / 4);
      if (bytes > r.remaining) throw new ProtocolError(`ARENA_TRAILS steps exceed the data`, nAt);
      for (let b = 0; b < bytes; b++) {
        const v = r.u8();
        for (let j = 0; j < 4 && b * 4 + j < k; j++) steps.push((v >> (2 * j)) & 3);
      }
    }
    out.push({ playerId, op: op as ArenaTrailOp, startCell, steps });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// ARENA_DROPS (0x13)
// ---------------------------------------------------------------------------------------------

export function writeArenaDropsSection(w: Writer, drops: ArenaDropsSection): void {
  checkCount(drops.added.length, MAX_ARENA_DROPS, 'ARENA_DROPS added');
  checkCount(drops.removed.length, MAX_ARENA_DROPS, 'ARENA_DROPS removed');
  const s = beginSection(w, SECTION.ARENA_DROPS);
  w.vu(drops.added.length);
  for (const d of drops.added) w.vu(d.dropId).vu(d.cell).vl(d.value);
  w.vu(drops.removed.length);
  for (const d of drops.removed) w.vu(d.dropId).vu(d.collectorId);
  endSection(w, s);
}

export function readArenaDropsSection(r: Reader): ArenaDropsSection {
  const na = readCount(r, MAX_ARENA_DROPS, 3, 'ARENA_DROPS added');
  const added: ArenaDropAdd[] = [];
  for (let i = 0; i < na; i++) added.push({ dropId: r.vu(), cell: r.vu(), value: r.vl() });
  const nr = readCount(r, MAX_ARENA_DROPS, 2, 'ARENA_DROPS removed');
  const removed: ArenaDropRemove[] = [];
  for (let i = 0; i < nr; i++) removed.push({ dropId: r.vu(), collectorId: r.vu() });
  return { added, removed };
}

// ---------------------------------------------------------------------------------------------
// ARENA_EVENTS (0x14)
// ---------------------------------------------------------------------------------------------

/** DEATH event flag bits. */
const DEATH_FLAG_AT_WALL = 1 << 0;

export function writeArenaEventRecord(w: Writer, e: ArenaEventRecord): void {
  w.u8(e.type);
  switch (e.type) {
    case ARENA_EVENT.CAPTURE:
      w.vu(e.playerId).vu(e.gainedCells).vu(e.cell);
      break;
    case ARENA_EVENT.DEATH:
      w.vu(e.victimId)
        .vu(e.killerId)
        .u8(e.cause)
        .u8(e.atWall ? DEATH_FLAG_AT_WALL : 0)
        .vl(e.value);
      break;
    case ARENA_EVENT.SPILL:
      w.vu(e.victimId).vu(e.dropCount).vl(e.value);
      break;
    case ARENA_EVENT.COLLECT:
      w.vu(e.playerId).vu(e.dropId).vl(e.value);
      break;
    case ARENA_EVENT.EXIT:
      w.vu(e.playerId).u8(e.exit).vl(e.value);
      break;
  }
}

export function writeArenaEventsSection(w: Writer, events: readonly ArenaEventRecord[]): void {
  checkCount(events.length, MAX_ARENA_EVENTS, 'ARENA_EVENTS');
  const s = beginSection(w, SECTION.ARENA_EVENTS);
  w.vu(events.length);
  for (const e of events) writeArenaEventRecord(w, e);
  endSection(w, s);
}

export function readArenaEventsSection(r: Reader): ArenaEventRecord[] {
  const n = readCount(r, MAX_ARENA_EVENTS, 4, 'ARENA_EVENTS');
  const out: ArenaEventRecord[] = [];
  for (let i = 0; i < n; i++) {
    const at = r.offset;
    const type = r.u8();
    switch (type) {
      case ARENA_EVENT.CAPTURE:
        out.push({ type, playerId: r.vu(), gainedCells: r.vu(), cell: r.vu() });
        break;
      case ARENA_EVENT.DEATH: {
        const victimId = r.vu();
        const killerId = r.vu();
        const causeAt = r.offset;
        const cause = r.u8();
        if (cause > MAX_CAUSE_CODE) {
          throw new ProtocolError(`unknown arena death cause ${cause}`, causeAt);
        }
        const flags = r.u8();
        out.push({
          type,
          victimId,
          killerId,
          cause: cause as ArenaCauseCode,
          atWall: (flags & DEATH_FLAG_AT_WALL) !== 0,
          value: r.vl(),
        });
        break;
      }
      case ARENA_EVENT.SPILL:
        out.push({ type, victimId: r.vu(), dropCount: r.vu(), value: r.vl() });
        break;
      case ARENA_EVENT.COLLECT:
        out.push({ type, playerId: r.vu(), dropId: r.vu(), value: r.vl() });
        break;
      case ARENA_EVENT.EXIT: {
        const playerId = r.vu();
        const exitAt = r.offset;
        const exit = r.u8();
        if (exit > MAX_EXIT_CODE)
          throw new ProtocolError(`unknown arena exit code ${exit}`, exitAt);
        out.push({ type, playerId, exit: exit as ArenaExitCode, value: r.vl() });
        break;
      }
      default:
        // No record length on the wire, so an unknown type cannot be skipped.
        throw new ProtocolError(`unknown ARENA_EVENTS type ${type}`, at);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// ARENA_OWN (0x15)
// ---------------------------------------------------------------------------------------------

export function writeArenaOwnSection(w: Writer, own: ArenaOwnSection): void {
  const s = beginSection(w, SECTION.ARENA_OWN);
  w.u32(own.landCells)
    .vu(own.kills)
    .u16(own.inputSeq & 0xffff);
  endSection(w, s);
}

export function readArenaOwnSection(r: Reader): ArenaOwnSection {
  return { landCells: r.u32(), kills: r.vu(), inputSeq: r.u16() };
}

// ---------------------------------------------------------------------------------------------
// ARENA_MINIMAP (0x16)
// ---------------------------------------------------------------------------------------------

/** One minimap head: `vu playerId`, `u8 x`, `u8 y`, `u8 flags` (bit0 isYou). */
export function writeArenaMinimapHeadRecord(
  w: Writer,
  playerId: number,
  x: number,
  y: number,
  isYou: boolean,
): void {
  w.vu(playerId)
    .u8(x)
    .u8(y)
    .u8(isYou ? 1 : 0);
}

/**
 * The minimap's `u8 size` and its cells as RLE of **symbols** (row-major, size × size): symbol 0 =
 * the same owner as the cell directly above (row 0: neutral), symbol v ≥ 1 = player id v − 1
 * (1 = neutral). Land is blobby, so most rows repeat the one above and collapse into long
 * symbol-0 runs. `runs` are the plain `[length, playerId, …]` cells (must cover size × size).
 */
export function writeArenaMinimapGrid(w: Writer, size: number, runs: readonly number[]): void {
  if (!(size >= 1 && size <= MAX_MINIMAP_SIZE)) throw new RangeError(`minimap size ${size}`);
  const total = size * size;
  if (runsTotal(runs) !== total) {
    throw new RangeError(`minimap runs cover ${runsTotal(runs)} of ${total} cells`);
  }
  const cells = new Array<number>(total).fill(0);
  expandRuns(runs, cells);
  const symbols = new Array<number>(total);
  for (let i = 0; i < total; i++) {
    const v = cells[i] as number;
    if (v >= 0xffffffff) throw new RangeError(`minimap player id ${v} too large`);
    symbols[i] = i >= size && cells[i - size] === v ? 0 : v + 1;
  }
  w.u8(size);
  writeRuns(w, rleRuns(symbols), 'ARENA_MINIMAP');
}

/** Reads the minimap symbols (see writeArenaMinimapGrid) back into plain cell runs. */
function readMinimapGrid(r: Reader, size: number): number[] {
  const total = size * size;
  const symRuns = readRuns(r, total, 'ARENA_MINIMAP');
  const cells = new Array<number>(total);
  let i = 0;
  for (let k = 0; k < symRuns.length; k += 2) {
    const len = symRuns[k] as number;
    const sym = symRuns[k + 1] as number;
    for (let j = 0; j < len; j++, i++) {
      cells[i] = sym !== 0 ? sym - 1 : i >= size ? (cells[i - size] as number) : 0;
    }
  }
  return rleRuns(cells);
}

export function writeArenaMinimapSection(w: Writer, mm: ArenaMinimapSection): void {
  checkCount(mm.heads.length, MAX_ARENA_HEADS, 'ARENA_MINIMAP heads');
  const s = beginSection(w, SECTION.ARENA_MINIMAP);
  writeArenaMinimapGrid(w, mm.size, mm.runs);
  w.vu(mm.heads.length);
  for (const h of mm.heads) writeArenaMinimapHeadRecord(w, h.playerId, h.x, h.y, h.isYou);
  endSection(w, s);
}

export function readArenaMinimapSection(r: Reader): ArenaMinimapSection {
  const sizeAt = r.offset;
  const size = r.u8();
  if (size === 0) throw new ProtocolError('ARENA_MINIMAP size 0', sizeAt);
  const runs = readMinimapGrid(r, size);
  const n = readCount(r, MAX_ARENA_HEADS, 4, 'ARENA_MINIMAP heads');
  const heads: ArenaMinimapHead[] = [];
  for (let i = 0; i < n; i++) {
    const playerId = r.vu();
    const x = r.u8();
    const y = r.u8();
    const flags = r.u8();
    heads.push({ playerId, x, y, isYou: (flags & 1) !== 0 });
  }
  return { size, runs, heads };
}
